import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export default function Pricing() {
  const [cards, setCards] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ category: "", base_rate: 0, unit: "per hour" });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/rate-cards"); setCards(data);
    const f = await api.get("/freelancers"); setFreelancers(f.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rate-cards", { ...form, base_rate: Number(form.base_rate) });
      setNewOpen(false); setForm({ category: "", base_rate: 0, unit: "per hour" });
      load(); toast.success("Rate card added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (rid) => {
    if (!window.confirm("Delete rate card?")) return;
    await api.delete(`/rate-cards/${rid}`); load(); toast.success("Deleted");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/rate-cards/${editing.id}`, {
        category: editing.category,
        base_rate: Number(editing.base_rate),
        unit: editing.unit,
        freelancer_override: editing.freelancer_override || {},
      });
      setEditing(null); load(); toast.success("Saved");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <PageHeader
        title="Pricing"
        description="Category base rates and per-freelancer overrides."
        actions={<button data-testid="new-rate-card-btn" onClick={() => setNewOpen(true)} className="bg-black text-white px-4 py-2 text-sm hard-shadow-hover">+ New Rate Card</button>}
      />
      <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map((c) => (
          <div key={c.id} className="border border-neutral-200 bg-white p-6 hard-shadow-hover" data-testid={`rate-card-${c.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="label-caps">{c.category}</div>
                <div className="mt-3 font-mono-tight text-3xl font-semibold">{formatINR(c.base_rate)} <span className="text-neutral-400 text-sm">{c.unit}</span></div>
              </div>
              <div className="flex gap-3">
                <button data-testid={`edit-rate-${c.id}`} onClick={() => setEditing({ ...c })} className="text-xs underline underline-offset-4">Edit</button>
                <button data-testid={`delete-rate-${c.id}`} onClick={() => remove(c.id)} className="text-xs underline underline-offset-4 text-[#FF2A00]">Delete</button>
              </div>
            </div>
            {Object.keys(c.freelancer_override || {}).length > 0 && (
              <div className="mt-4">
                <div className="label-caps mb-1">Overrides</div>
                <ul className="text-sm space-y-1">
                  {Object.entries(c.freelancer_override).map(([fid, rate]) => (
                    <li key={fid} className="flex justify-between font-mono-tight"><span>{freelancers.find((f) => f.id === fid)?.name || fid}</span><span>{formatINR(rate)}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
        {cards.length === 0 && <div className="text-neutral-500 col-span-2 text-center py-10">No rate cards yet.</div>}
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none border-2 border-black">
          <DialogHeader><DialogTitle className="font-display text-2xl">New Rate Card</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><label className="label-caps block mb-1">Category</label><Input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none border-black" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Base Rate (INR)</label><Input required type="number" value={form.base_rate} onChange={(e) => setForm({ ...form, base_rate: e.target.value })} className="rounded-none border-black" /></div>
              <div><label className="label-caps block mb-1">Unit</label>
                <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                  <option>per hour</option><option>per day</option><option>per project</option>
                </select>
              </div>
            </div>
            <DialogFooter><button type="submit" className="bg-black text-white px-4 py-2 text-sm">Create</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none border-2 border-black">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Edit Rate Card</DialogTitle></DialogHeader>
              <form onSubmit={saveEdit} className="space-y-3">
                <div><label className="label-caps block mb-1">Category</label><Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-none border-black" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label-caps block mb-1">Base Rate</label><Input type="number" value={editing.base_rate} onChange={(e) => setEditing({ ...editing, base_rate: e.target.value })} className="rounded-none border-black" /></div>
                  <div><label className="label-caps block mb-1">Unit</label><Input value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} className="rounded-none border-black" /></div>
                </div>
                <div>
                  <label className="label-caps block mb-1">Per-Freelancer Overrides</label>
                  <div className="border border-black p-2 max-h-56 overflow-auto space-y-1">
                    {freelancers.map((f) => (
                      <div key={f.id} className="flex items-center gap-2">
                        <span className="text-sm flex-1">{f.name}</span>
                        <Input type="number" placeholder="Override rate" value={editing.freelancer_override?.[f.id] || ""} onChange={(e) => {
                          const v = e.target.value;
                          const next = { ...(editing.freelancer_override || {}) };
                          if (v === "") delete next[f.id]; else next[f.id] = Number(v);
                          setEditing({ ...editing, freelancer_override: next });
                        }} className="rounded-none border-black w-32 h-8 text-sm" />
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter><button type="submit" className="bg-black text-white px-4 py-2 text-sm">Save</button></DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
